import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, MenuItem, Divider, Paper, Chip, useMediaQuery } from '@mui/material';
import { Eye } from 'lucide-react';
import theme from '../../util/theme/theme';
import { SlideUpTransition } from './mobile/mobile-bottom-sheet';
import { useSubscriptionTiers } from '../../util/subscription-tiers';
import CompanyUsersPanel, { CompanyUserRow } from './parts/company-users-panel';
import Toggle from '../ui/toggle';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { formatDate } from '../../util/date';
import { useToast } from '../../providers/toaster-provider';

type CompanyRow = Record<string, any>;

type CompanyDetailsModalProps = {
    open: boolean;
    company: CompanyRow | null;
    onClose: () => void;
};

type SubscriptionStatusResponse = {
    tierId: string;
    status: 'active' | 'expired';
    startedAt: string;
    renewsOn: string | null;
    autoRenew: boolean;
    trialEndsOn: string | null;
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Typography fontSize={12} color="var(--color-text-muted)">{label}</Typography>
        <Typography fontSize={14} fontWeight={500}>{value}</Typography>
    </Box>
);

// Company fields here (country, banks, trx, payment, created, lastLogin,
// access, account) come straight from the real /entities row this page
// already fetches. The Subscription section is now real data too (the
// EntitySubscription table) - Plan is the one thing an admin can change
// here, via the admin-only /admin-set endpoint (setEntitySubscription
// without generating a purchase invoice - this is a correction, not a
// sale). Status/renewal/trial-end are the same computed values the Profile
// page itself shows, read-only here since there's no real billing backend
// to arbitrarily set them against. The Users side panel is real data too -
// the same entityUsers association getEntityById (routes.company.fetchById)
// already returns - fetched fresh whenever this opens.
const CompanyDetailsModal: React.FC<CompanyDetailsModalProps> = ({
    open, company, onClose,
}) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { showSuccess, showError } = useToast();
    const [showUsersPanel, setShowUsersPanel] = useState(false);
    const [fetchDetails, setFetchDetails] = useApi<any>();
    const [fetchSubscription, setFetchSubscription] = useApi<SubscriptionStatusResponse>();
    const [adminSetResult, setAdminSet] = useApi<{ success: boolean }>();
    const [, setToggleAutoRenew] = useApi<{ success: boolean }>();
    const [selectedTierId, setSelectedTierId] = useState<string>('');
    const [autoRenewOverride, setAutoRenewOverride] = useState<boolean | null>(null);
    const { tiers } = useSubscriptionTiers();

    useEffect(() => {
        if (open && company?.id) {
            setFetchDetails(buildApiRequestConfig(routes.company.fetchById, [{ param: 'id', value: company.id }]));
            setFetchSubscription(buildApiRequestConfig(routes.subscription.fetchStatus, [{ param: 'entityId', value: company.id }]));
            setAutoRenewOverride(null);
        } else {
            setShowUsersPanel(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, company?.id]);

    useEffect(() => {
        if (fetchSubscription.data) setSelectedTierId(fetchSubscription.data.tierId);
    }, [fetchSubscription.data]);

    const refreshSubscription = () => {
        if (!company?.id) return;
        setFetchSubscription(buildApiRequestConfig(routes.subscription.fetchStatus, [{ param: 'entityId', value: company.id }]));
    };

    useEffect(() => {
        if (adminSetResult.data && !adminSetResult.isLoading) {
            showSuccess('Plan updated');
            refreshSubscription();
        }
        if (adminSetResult.error) showError(typeof adminSetResult.error === 'string' ? adminSetResult.error : 'Failed to update plan');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminSetResult.data, adminSetResult.isLoading, adminSetResult.error]);

    if (!company) return null;

    const users: CompanyUserRow[] = (fetchDetails.data?.entityUsers ?? []).map((eu: any) => ({
        id: eu.id,
        name: eu.name || (eu.user ? `${eu.user.firstName ?? ''} ${eu.user.lastName ?? ''}`.trim() : null),
        email: eu.user?.email ?? eu.invitedEmail ?? null,
        role: eu.role,
        status: eu.status,
        isActive: eu.isActive,
    }));
    const usersPanelVisible = showUsersPanel;

    const subscription = fetchSubscription.data;
    const currentTier = tiers.find(t => t.id === subscription?.tierId);
    const autoRenew = autoRenewOverride ?? subscription?.autoRenew ?? true;
    const planDirty = !!subscription && selectedTierId !== subscription.tierId;

    const handleSavePlan = () => {
        if (!company?.id || !selectedTierId) return;
        const { url, method } = buildApiRequestConfig(routes.subscription.adminSet, [{ param: 'entityId', value: company.id }]);
        setAdminSet({ url, method, body: { tierId: selectedTierId } });
    };

    const handleToggleAutoRenew = (checked: boolean) => {
        setAutoRenewOverride(checked);
        if (!company?.id) return;
        const { url, method } = buildApiRequestConfig(routes.subscription.toggleAutoRenew, [{ param: 'entityId', value: company.id }]);
        setToggleAutoRenew({ url, method, body: { autoRenew: checked } });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={isMobile}
            maxWidth={false}
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                // Transparent flex container - the Users panel and the main
                // details card each render as their own Paper, so they read
                // as two adjacent modals (same technique as the Occurrences
                // panel beside Edit Provision) rather than one wide box.
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '90vh' }
                    : { background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 0.25, margin: 'auto', maxWidth: 'none', maxHeight: '90vh' }
            }}
        >
            <Paper sx={{
                borderRadius: isMobile ? '16px 16px 0 0' : (usersPanelVisible ? '12px 0 0 12px' : 3),
                width: isMobile ? '100%' : 480,
                maxHeight: isMobile ? (usersPanelVisible ? '55vh' : '90vh') : '90vh',
                overflowY: 'auto',
            }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: isMobile ? 16 : 20, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{company.company}</span>
                    <Button
                        size="small"
                        startIcon={<Eye size={16} />}
                        onClick={() => setShowUsersPanel(v => !v)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 13,
                            borderRadius: 2,
                            px: 1.25,
                            py: 0.25,
                            minWidth: 0,
                            color: showUsersPanel ? 'var(--color-primary)' : 'var(--color-text-muted)',
                            background: showUsersPanel ? 'rgba(var(--color-primary-rgb), 0.1255)' : 'var(--color-container-background)',
                            '&:hover': {
                                background: showUsersPanel ? 'rgba(var(--color-primary-rgb), 0.251)' : 'var(--color-container-background)',
                            },
                        }}
                    >
                        Users
                    </Button>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={3} mt={1}>
                        <Box display="grid" gridTemplateColumns={isMobile ? '1fr 1fr' : '1fr 1fr 1fr'} gap={2}>
                            <DetailRow label="Country" value={company.country ?? '—'} />
                            <DetailRow label="Contact Number" value={company.phone ?? '—'} />
                            <DetailRow label="Bank Accounts" value={company.banks ?? '—'} />
                            <DetailRow label="Transactions" value={company.trx ?? '—'} />
                            <DetailRow label="Payment Status" value={company.payment ?? '—'} />
                            <DetailRow label="Created" value={company.created ?? '—'} />
                            <DetailRow label="Last Login" value={company.lastLogin ?? '—'} />
                            <DetailRow label="Access" value={company.access ? 'Login Permitted' : 'Login Restricted'} />
                            <DetailRow label="Account" value={company.account ? 'Open' : 'Closed'} />
                        </Box>

                        <Divider />

                        <Box>
                            <Typography fontSize={14} fontWeight={700} mb={2}>Subscription</Typography>
                            {!subscription ? (
                                <Typography fontSize={12} color="var(--color-text-muted)">Loading…</Typography>
                            ) : (
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Box display="flex" gap={1} alignItems="flex-end">
                                        <Box flex={1}>
                                            <Typography mb={1} fontSize={13} color="var(--color-text-muted)">Plan</Typography>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                value={selectedTierId}
                                                onChange={(e) => setSelectedTierId(e.target.value)}
                                            >
                                                {tiers.map(t => (
                                                    <MenuItem key={t.id} value={t.id}>
                                                        {t.billing ? `${t.name} - ${t.billing === 'annual' ? '12mo up front' : t.billing === 'daily' ? 'Daily (QA)' : 'No commitment'}` : t.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={!planDirty || adminSetResult.isLoading}
                                            onClick={handleSavePlan}
                                            sx={{ height: 36, background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none', flexShrink: 0, '&:hover': { background: 'var(--color-primary)' } }}
                                        >
                                            Apply
                                        </Button>
                                    </Box>
                                    <Typography fontSize={10.5} color="var(--color-text-muted)" sx={{ mt: -1.25 }}>
                                        Changes the entity's actual plan directly - no invoice is created, this is a correction, not a sale.
                                    </Typography>

                                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                                        <DetailRow
                                            label="Status"
                                            value={
                                                <Chip
                                                    size="small"
                                                    label={subscription.status === 'expired' ? 'Trial Expired' : 'Active'}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        color: '#fff',
                                                        backgroundColor: subscription.status === 'expired' ? 'var(--color-negative-amount)' : '#1a7f37',
                                                    }}
                                                />
                                            }
                                        />
                                        <DetailRow
                                            label={currentTier?.billing ? 'Renews On' : 'Trial Ends On'}
                                            value={
                                                currentTier?.billing
                                                    ? (subscription.renewsOn ? formatDate(subscription.renewsOn) : '—')
                                                    : (subscription.trialEndsOn ? formatDate(subscription.trialEndsOn) : '—')
                                            }
                                        />
                                    </Box>

                                    {currentTier?.billing && (
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Toggle checked={autoRenew} onChange={handleToggleAutoRenew} size="small" />
                                            <Typography fontSize={13}>Auto-Renew</Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={onClose}
                        variant="contained"
                        sx={{ background: 'var(--color-container-background)', color: 'var(--color-default-text)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Paper>

            {usersPanelVisible && (isMobile ? (
                <Paper sx={{
                    borderRadius: '0 0 16px 16px',
                    width: '100%',
                    flexShrink: 0,
                    maxHeight: '35vh',
                    display: 'flex',
                    overflow: 'hidden',
                }}>
                    <CompanyUsersPanel
                        users={users}
                        isLoading={fetchDetails.isLoading}
                        onClose={() => setShowUsersPanel(false)}
                    />
                </Paper>
            ) : (
                // Empty positioning spacer (see ProvisionOccurrencesPanel's own
                // comment for why): align-items 'stretch' on the row makes it
                // match the main card's height, and the real card fills it via
                // position:absolute + inset:0, so the main card alone drives
                // the pair's height.
                <Box sx={{ position: 'relative', width: 280, flexShrink: 0 }}>
                    <Paper sx={{
                        borderRadius: '0 12px 12px 0',
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        overflow: 'hidden',
                    }}>
                        <CompanyUsersPanel
                            users={users}
                            isLoading={fetchDetails.isLoading}
                            onClose={() => setShowUsersPanel(false)}
                        />
                    </Paper>
                </Box>
            ))}
        </Dialog>
    );
};

export default CompanyDetailsModal;
